import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Server, Plus, Search, Trash2 } from 'lucide-react';

import { BACKEND_URL } from '@/lib/backend';
export default function DevicesModule() {
  const [devices, setDevices] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    organization_id: '', device_name: '', device_type: 'server',
    manufacturer: '', model: '', serial_number: '', ip_address: '',
    mac_address: '', operating_system: '', location: '', notes: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const [devRes, orgRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/documentation/devices`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/documentation/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (devRes.ok) setDevices(await devRes.json());
      if (orgRes.ok) setOrganizations(await orgRes.json());
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowAddDialog(false);
        loadData();
      }
    } catch (err) {
      console.error('Failed to create device:', err);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete ${name}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/documentation/devices/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) loadData();
    } catch (err) {
      console.error('Failed to delete device:', err);
    }
  };

  const filtered = devices.filter(d =>
    d.device_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Devices</h1>
          <p className="text-sm text-slate-500 mt-1">Hardware & equipment tracking</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <Button onClick={() => setShowAddDialog(true)} className="bg-cyan-600 hover:bg-cyan-700">
            <Plus className="w-4 h-4 mr-2" />Add Device
          </Button>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Add New Device</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Organization *</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({...formData, organization_id: v})} required>
                  <SelectTrigger><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Device Name *</Label><Input value={formData.device_name} onChange={(e) => setFormData({...formData, device_name: e.target.value})} required /></div>
                <div><Label>Type</Label>
                  <Select value={formData.device_type} onValueChange={(v) => setFormData({...formData, device_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="server">Server</SelectItem>
                      <SelectItem value="workstation">Workstation</SelectItem>
                      <SelectItem value="router">Router</SelectItem>
                      <SelectItem value="firewall">Firewall</SelectItem>
                      <SelectItem value="switch">Switch</SelectItem>
                      <SelectItem value="printer">Printer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Manufacturer</Label><Input value={formData.manufacturer} onChange={(e) => setFormData({...formData, manufacturer: e.target.value})} /></div>
                <div><Label>Model</Label><Input value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} /></div>
                <div><Label>Serial Number</Label><Input value={formData.serial_number} onChange={(e) => setFormData({...formData, serial_number: e.target.value})} /></div>
                <div><Label>IP Address</Label><Input value={formData.ip_address} onChange={(e) => setFormData({...formData, ip_address: e.target.value})} /></div>
                <div><Label>MAC Address</Label><Input value={formData.mac_address} onChange={(e) => setFormData({...formData, mac_address: e.target.value})} /></div>
                <div><Label>OS</Label><Input value={formData.operating_system} onChange={(e) => setFormData({...formData, operating_system: e.target.value})} /></div>
                <div className="col-span-2"><Label>Location</Label><Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} /></div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">Create Device</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-6"><CardContent className="p-4"><div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" /><Input placeholder="Search devices..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" /></div></CardContent></Card>

      {loading ? <div className="text-center py-12"><p className="text-slate-500">Loading...</p></div> : filtered.length === 0 ? <Card><CardContent className="p-12 text-center"><Server className="w-12 h-12 mx-auto text-slate-400 mb-3" /><p className="text-slate-600 font-medium">No devices found</p></CardContent></Card> : (
        <div className="space-y-3">
          {filtered.map((device) => (
            <Card key={device.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Server className="w-5 h-5 text-cyan-600" />
                      <h3 className="font-semibold text-slate-800">{device.device_name}</h3>
                      <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-1 rounded">{device.device_type}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><p className="text-slate-500">Organization</p><p className="font-medium">{device.organization_name}</p></div>
                      {device.manufacturer && <div><p className="text-slate-500">Manufacturer</p><p className="font-medium">{device.manufacturer}</p></div>}
                      {device.ip_address && <div><p className="text-slate-500">IP Address</p><p className="font-mono text-xs">{device.ip_address}</p></div>}
                      {device.operating_system && <div><p className="text-slate-500">OS</p><p className="font-medium">{device.operating_system}</p></div>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(device.id, device.device_name)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
